import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import {
  getContactPurchasePayload,
  handlePurchaseResponse,
  getSelectedContactsPayload,
  getAddContactsToSequencePayload,
  DISCOVER_TOTAL_COUNT_CAP
} from "./DataSearchUtils";
import { PaywallUtils } from "Utils/PaywallUtils";
import { observable, action } from "mobx";
import { customListState } from "./CustomList/state";
import { popupLoaderState } from "./PopupLoader";
import { Utils } from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import { exportProcessingState } from "Components/popup/ExportProcessing";
import { ContactCardFunction } from "Components/ContactCard/ContactCardFunction";
import { IntercomTour } from "Static/constant";
import { getFeatureFlagWithTeam } from "Utils/commonAPI";
import { userDetail } from "Stores/userDetail";
import { upgradePopupState } from "Components/Paywall/UpgradePopup";
import bulkJobsNotificationStore from "Stores/bulkJobsNotification";
import { DEFAULT_EXPERIENCE_DURATION } from "./filterComponent/filterComponentData";

/** Thrown when add-to-list is aborted due to 402 (upgrade popup shown). Callers should catch and not show toaster. */
export const ADD_TO_LIST_UPGRADE_REQUIRED = "ADD_TO_LIST_UPGRADE_REQUIRED";

let cancelRequest = null;
let technologiesListCache = null;
let industriesCatalogCache = null;

const getTechnologiesList = async () => {
  if (technologiesListCache) return technologiesListCache;
  const res = await makeApi({ url: URLS.technologiesList, method: "GET" });
  const list = res?.data;
  technologiesListCache = Array.isArray(list) ? list : [];
  return technologiesListCache;
};

const getIndustriesCatalog = async () => {
  if (industriesCatalogCache) return industriesCatalogCache;
  const res = await makeApi({ url: URLS.industriesListV2, method: "GET" });
  const payload = res?.data || {};
  industriesCatalogCache = {
    industries: Array.isArray(payload?.industries) ? payload.industries : [],
    topIndustries: Array.isArray(payload?.topIndustries) ? payload.topIndustries : []
  };
  return industriesCatalogCache;
};

const LIST_BUILDER_STORAGE_KEY = "sg-list-builder-filters";
/** localStorage key for "last time we did auto-search on first filter"; used to only auto-search once per 24h */
const LIST_BUILDER_AUTO_SEARCH_AT_KEY = "sg-list-builder-last-auto-search-at";
const AUTO_SEARCH_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Same shape as FilterComponent `getDefaultFilterState` — base for extension import merge. */
function createDefaultFilterComponentDataForImport() {
  return {
    confidenceScore: [],
    excludeTeamOwns: true,
    industry: [],
    industryExclude: [],
    sector: [],
    employeeCount: [],
    revenue: [],
    department: [],
    funding: [],
    fundingTotalAmountStart: null,
    fundingTotalAmountEnd: null,
    fundingLastAmountStart: null,
    fundingLastAmountEnd: null,
    fundingDurationStart: null,
    fundingDurationEnd: null,
    employeesByDepartment: [],
    growthByDepartment: [],
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    technologies: [],
    level: [],
    title: [],
    titleExclude: [],
    skill: [],
    skillExclude: [],
    certification: [],
    certificationExclude: [],
    titleExactMatch: false,
    primaryActiveRoleOnly: true,
    companyDomain: [],
    companyName: [],
    companyDomainList: [],
    companyNameList: [],
    companyLinkedIn: [],
    companyLinkedInList: [],
    companySearchType: "domain",
    companyKeywords: [],
    companyKeywordsExclude: [],
    contactKeywords: [],
    contactKeywordsExclude: [],
    profileBadge: [],
    ...DEFAULT_EXPERIENCE_DURATION,
    country: [],
    state: [],
    city: [],
    companyCountry: [],
    companyState: [],
    companyCity: [],
    name: [],
    contactLinkedIn: [],
    contactSearchType: "name",
    excludeContactsMode: "list",
    excludeContactListId: null,
    excludeContactListName: "",
    excludeContactLinkedIn: []
  };
}

/** Decode `?sgLb=` from SalesGear extension (matches `btoa(unescape(encodeURIComponent(json)))` + base64url). */
export function decodeListBuilderExtensionImportParam(raw) {
  if (!raw || typeof raw !== "string") return null;
  try {
    let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? 4 - (b64.length % 4) : 0;
    b64 += "=".repeat(pad);
    const str = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(str);
  } catch (e) {
    console.warn("List Builder: could not decode sgLb", e);
    return null;
  }
}

const checkAndCancelRequest = () => {
  if (cancelRequest !== null) {
    cancelRequest("cancel");
    cancelRequest = null;
  }
};

const cancelApiRequest = value => {
  cancelRequest = value;
};

/** Normalized LinkedIn URL from a discover contact (for owned-data lookup). */
const getLinkedInUrlFromContact = contact => {
  const url = contact?.linkedIn ?? contact?.linkedin ?? contact?.linkedInProfileUrl ?? "";
  return typeof url === "string" ? url.trim() : "";
};

class DataSearchState {
  @observable filterComponentData = {
    confidenceScore: [],
    excludeTeamOwns: true,
    primaryActiveRoleOnly: true,
    titleExclude: [],
    skill: [],
    skillExclude: [],
    certification: [],
    certificationExclude: [],
    industry: [],
    industryExclude: [],
    companyKeywordsExclude: [],
    contactKeywordsExclude: [],
    profileBadge: [],
    experienceCurrentJobMinYear: 0,
    experienceCurrentJobMaxYear: 0,
    experienceCurrentJobMinMonth: 0,
    experienceCurrentJobMaxMonth: 0,
    experienceCurrentCompanyMinYear: 0,
    experienceCurrentCompanyMaxYear: 0,
    experienceCurrentCompanyMinMonth: 0,
    experienceCurrentCompanyMaxMonth: 0,
    experienceTotalMinYear: 0,
    experienceTotalMaxYear: 0,
    experienceTotalMinMonth: 0,
    experienceTotalMaxMonth: 0,
    fundingTotalAmountStart: null,
    fundingTotalAmountEnd: null,
    fundingLastAmountStart: null,
    fundingLastAmountEnd: null,
    fundingDurationStart: null,
    fundingDurationEnd: null,
    employeesByDepartment: [],
    growthByDepartment: [],
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    excludeContactsMode: "list",
    excludeContactListId: null,
    excludeContactListName: "",
    excludeContactLinkedIn: []
  };
  @observable activeSearchTab = "contacts";
  @observable contactTableData = {};
  @observable companyTableData = {};
  @observable companyCurrentPage = 1;
  @observable companyTableLoading = false;
  @observable companyShowInitialPage = true;
  /** Snapshot of filterComponentData when last company search was run (for stale-banner logic) */
  @observable companyLastSearchedFilters = null;
  @observable selectedContactData = [];
  @observable pageLimit = 10;
  @observable currentPage = 1;
  @observable selectedFilteredList = {};
  /** Current search input text per filter (e.g. companyLocation, industry); used to hide field hint when user is typing. */
  @observable filterPanelSearchQuery = {};
  _persistFiltersTimeout = null;
  @observable industriesList = [];
  @observable isFilterApplied = false;
  @observable clearAll = false;
  @observable rangeSelected = "";
  @observable isSelectionReset = false;
  @observable isFromSeoPage = false;
  @observable isEmailViewedFromSeoPage = false;
  @observable hasData = false;
  @observable showInitialPage = true;
  @observable discoverTableLoading = false;
  @observable isFindPhoneEmailClicked = false;
  @observable findDataType = "";
  @observable singlePurchaseContactName = "";
  @observable singlePurchaseContactMail = "";
  @observable foundData = false;
  @observable contactId = "";
  @observable mobilePhone = "";
  @observable isMobileNumberNotFound = true;
  @observable isPhonePurchase = false;
  @observable isNoPhoneCredits = false;
  @observable totalEnrichContactsCount = 0;
  @observable contactCompanyName = "";
  @observable foundMaskedNumber = false;
  /** Snapshot of filterComponentData when last search was run; used to show "results outdated" banner */
  @observable lastSearchedFilters = null;
  /** Increments each time the filter slider should be forced closed (e.g. Refresh clicked) */
  @observable closeFilterSliderSignal = 0;
  /** Experience duration validation errors by section (currentJob, currentCompany, total); used to block search when set */
  @observable experienceDurationErrors = {};
  /** Employees by Department validation: index -> true when Max < Min */
  @observable employeesByDepartmentErrors = {};
  /** Growth by Department validation: index -> true when Max < Min */
  @observable growthByDepartmentErrors = {};
  @observable excludeContactsLoading = false;

  /**
   * True when current filters differ from the filters used for the last search (results are stale).
   */
  getFiltersDirtySinceLastSearch = () => {
    const prev = this.lastSearchedFilters;
    if (prev == null) return false;
    const curr = this.filterComponentData;
    const keys = new Set([...Object.keys(prev), ...Object.keys(curr)]);
    for (const k of keys) {
      if (JSON.stringify(prev[k]) !== JSON.stringify(curr[k])) return true;
    }
    return false;
  };

  /** True when closing a filter panel should trigger search (filters changed or first search pending). */
  shouldAutoSearchOnFilterClose = () => {
    if (!this.hasAnyFilterSelected()) return false;
    if (this.activeSearchTab === "companies") {
      if (this.companyShowInitialPage) return true;
      return this.getCompanyFiltersDirtySinceLastSearch();
    }
    if (this.showInitialPage) return true;
    return this.getFiltersDirtySinceLastSearch();
  };

  /** Run search for the active tab; resets pagination and closes the filter slider. */
  @action
  applyFiltersAndSearch = async () => {
    if (!this.hasAnyFilterSelected()) return false;
    this.setCurrentPage(1);
    this.setCompanyCurrentPage(1);
    this.setIsFromSeoPage(false);
    this.triggerCloseFilterSlider();
    if (this.activeSearchTab === "companies") {
      return await this.searchCompanyData();
    }
    return await this.searchPageData();
  };

  @action
  setLastSearchedFilters = val => {
    this.lastSearchedFilters = val;
  };

  @action
  triggerCloseFilterSlider = () => {
    this.closeFilterSliderSignal += 1;
  };

  @action
  setClearAll = val => {
    this.clearAll = val;
  };

  @action
  setShowInitialPage = val => {
    this.showInitialPage = val;
  };

  /** Call when Refresh is clicked with no filters selected: show initial window and clear stale state. */
  @action
  resetToInitialView = () => {
    this.setShowInitialPage(true);
    this.setContactTableData({});
    this.lastSearchedFilters = null;
  };

  @action
  setSelectedContactData = val => {
    this.selectedContactData = val;
  };

  @action
  setPageLimit = val => {
    this.pageLimit = val;
    this._persistFiltersToStorage();
  };

  @action
  setCurrentPage = val => {
    this.currentPage = val;
    this._persistFiltersToStorage();
  };

  _persistFiltersToStorage = () => {
    if (this._persistFiltersTimeout) clearTimeout(this._persistFiltersTimeout);
    this._persistFiltersTimeout = setTimeout(() => {
      try {
        const payload = {
          filterComponentData: this.filterComponentData,
          selectedFilteredList: this.selectedFilteredList,
          currentPage: this.currentPage,
          pageLimit: this.pageLimit
        };
        localStorage.setItem(LIST_BUILDER_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        console.warn("Discover V2: could not persist filters", e);
      }
      this._persistFiltersTimeout = null;
    }, 300);
  };

  @action
  loadPersistedFilters = () => {
    try {
      let raw = localStorage.getItem(LIST_BUILDER_STORAGE_KEY);
      if (!raw) raw = localStorage.getItem("sg-discover-v2-filters"); // backward compat
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.filterComponentData && typeof data.filterComponentData === "object") {
        this.filterComponentData = {
          confidenceScore: [],
          excludeTeamOwns: true,
          industry: [],
          ...data.filterComponentData
        };
        const hasFilters = Object.entries(data.filterComponentData).some(([k, v]) => {
          if ([
            "confidenceScore", "excludeTeamOwns", "industry",
            "excludeContactsMode", "excludeContactListId", "excludeContactListName"
          ].includes(k)) return false;
          return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "";
        });
        this.setFilterApplied(!!hasFilters);
      }
      if (data.selectedFilteredList && typeof data.selectedFilteredList === "object") {
        this.selectedFilteredList = data.selectedFilteredList;
      }
      const sl = this.selectedFilteredList || {};
      const fd = this.filterComponentData;
      if (!(sl.location || []).length) {
        fd.country = [];
        fd.state = [];
        fd.city = [];
      }
      if (!(sl.companyLocation || []).length) {
        fd.companyCountry = [];
        fd.companyState = [];
        fd.companyCity = [];
      }
      const cst = fd.contactSearchType || "name";
      if (cst === "linkedin") {
        fd.name = [];
      } else {
        fd.contactLinkedIn = [];
      }
      if (typeof data.currentPage === "number" && data.currentPage >= 1) {
        this.currentPage = data.currentPage;
      }
      if (typeof data.pageLimit === "number" && data.pageLimit >= 1) {
        this.pageLimit = data.pageLimit;
      }
    } catch (e) {
      console.warn("Discover V2: could not load persisted filters", e);
    }
  };

  /**
   * Replace List Builder filters with payload from the SalesGear extension (LinkedIn company view),
   * clear saved filters in localStorage, and run search.
   */
  @action
  applyFiltersFromCompanyExtensionImport = async importedFilters => {
    try {
      localStorage.removeItem(LIST_BUILDER_STORAGE_KEY);
      localStorage.removeItem("sg-discover-v2-filters");
    } catch (e) {
      /* ignore */
    }
    const base = createDefaultFilterComponentDataForImport();
    const incoming =
      importedFilters && typeof importedFilters === "object" ? importedFilters : {};
    this.filterComponentData = { ...base, ...incoming };
    this.selectedFilteredList = {};
    this.currentPage = 1;
    this.pageLimit = 10;
    this.setFilterApplied(this.hasAnyFilterSelected());
    this.setContactTableData({});
    this.setLastSearchedFilters(null);
    this.setExperienceDurationErrors({});
    this.setEmployeesByDepartmentErrors({});
    this.setGrowthByDepartmentErrors({});
    this.setShowInitialPage(false);
    this.setClearAll(false);
    this.setIsFromSeoPage(false);
    await this.searchPageData(false);
  };

  /**
   * Apply List Builder filters after SEO contact-page signup (cookie `sg-seo-lb`).
   * Merges `preparedPartial` over default filters, clears persisted LB storage, marks SEO session, runs search.
   * @param {Object} preparedPartial — e.g. { contactSearchType, name, contactLinkedIn }
   */
  @action
  applyFiltersFromSeoListBuilderImport = async preparedPartial => {
    try {
      localStorage.removeItem(LIST_BUILDER_STORAGE_KEY);
      localStorage.removeItem("sg-discover-v2-filters");
    } catch (e) {
      /* ignore */
    }
    const base = createDefaultFilterComponentDataForImport();
    const incoming =
      preparedPartial && typeof preparedPartial === "object" ? preparedPartial : {};
    this.filterComponentData = { ...base, ...incoming };
    this.selectedFilteredList = {};
    this.currentPage = 1;
    this.pageLimit = 10;
    this.setFilterApplied(this.hasAnyFilterSelected());
    this.setContactTableData({});
    this.setLastSearchedFilters(null);
    this.setExperienceDurationErrors({});
    this.setEmployeesByDepartmentErrors({});
    this.setGrowthByDepartmentErrors({});
    this.setShowInitialPage(false);
    this.setClearAll(false);
    this.setIsFromSeoPage(true);
    await this.searchPageData(false);
  };

  @action
  setFilterComponentData = val => {
    const wasOnInitialPage = this.showInitialPage;
    this.filterComponentData = val;
    this.setFilterApplied(true);
    this._persistFiltersToStorage();
    // When the user selects their first filter from the initial page:
    //   - First time in 24h: auto-search immediately.
    //   - Within 24h cooldown: exit initial page and enter "dirty" state so the Refresh banner
    //     appears and the user knows they need to click Search/Refresh to get results.
    if (wasOnInitialPage && this.hasAnyFilterSelected()) {
      let allowAutoSearch = false;
      try {
        const raw = localStorage.getItem(LIST_BUILDER_AUTO_SEARCH_AT_KEY);
        const lastAt = raw ? parseInt(raw, 10) : 0;
        if (!Number.isFinite(lastAt) || Date.now() - lastAt > AUTO_SEARCH_COOLDOWN_MS) {
          allowAutoSearch = true;
          localStorage.setItem(LIST_BUILDER_AUTO_SEARCH_AT_KEY, String(Date.now()));
        }
      } catch (_) {
        allowAutoSearch = true; // if storage fails, allow auto-search
      }
      if (allowAutoSearch) {
        this.setShowInitialPage(false);
        this.searchPageData();
      } else {
        // Stay on the initial page but mark dirty so the "Filters selected – Search" banner
        // appears on top of the initial page content. No API call is made.
        this.lastSearchedFilters = {};
      }
    }
  };

  @action
  setContactTableData = val => {
    this.contactTableData = val;
  };

  /**
   * Fetches email/phone for contacts already in "My purchased contacts" and merges into current table.
   * Called after search results load so owned email/mobile display without extra clicks.
   */
  mergeOwnedContactData = async contacts => {
    const list = Array.isArray(contacts) ? contacts : [];
    const linkedInUrls = list
      .map(c => getLinkedInUrlFromContact(c))
      .filter(Boolean);
    if (linkedInUrls.length === 0) return;
    try {
      const res = await makeApi({
        url: URLS.getDiscoverOwnedContacts,
        method: "POST",
        data: { linkedInUrls }
      });
      const owned = res?.data?.owned;
      if (typeof owned !== "object" || Object.keys(owned).length === 0) return;
      const current = Utils.createObjWithoutRef(this.contactTableData) ?? {};
      const currentContacts = current?.contacts ?? [];
      const toPhoneObjArray = arr => {
        if (!Array.isArray(arr)) return [];
        return arr
          .map(item =>
            typeof item === "string"
              ? { number: item }
              : item && typeof item === "object" && "number" in item
                ? item
                : null
          )
          .filter(Boolean);
      };
      const updated = currentContacts.map(c => {
        const url = getLinkedInUrlFromContact(c);
        const data = url ? owned[url] : null;
        if (!data) return c;
        const directPhone = toPhoneObjArray(data.directPhone).length > 0 ? toPhoneObjArray(data.directPhone) : (c.directPhone ?? []);
        const mobilePhone = toPhoneObjArray(data.mobilePhone).length > 0 ? toPhoneObjArray(data.mobilePhone) : (c.mobilePhone ?? []);
        const otherPhone = toPhoneObjArray(data.otherPhone).length > 0 ? toPhoneObjArray(data.otherPhone) : (c.otherPhone ?? []);
        const hasPhone = directPhone.length > 0 || mobilePhone.length > 0 || otherPhone.length > 0;
        return {
          ...c,
          email: data.email ?? c.email,
          emailOwned: data.emailOwned === true || c.emailOwned,
          directPhone,
          mobilePhone,
          otherPhone,
          phoneOwned: data.phoneOwned === true || c.phoneOwned || hasPhone
        };
      });
      this.setContactTableData({ ...current, contacts: updated });
    } catch (_) {
      // Non-blocking; table already shows results
    }
  };

  @action
  setSelectedFilteredList = val => {
    this.selectedFilteredList = val;
    this._persistFiltersToStorage();
  };

  @action
  setFilterPanelSearchQuery = updates => {
    if (updates && typeof updates === "object") {
      this.filterPanelSearchQuery = { ...this.filterPanelSearchQuery, ...updates };
    }
  };

  @action
  setIndustriesList = val => {
    this.industriesList = val;
  };

  @action
  setFilterApplied = val => {
    this.isFilterApplied = val;
  };

  @action
  setRangeSelected = (obj = "") => {
    this.rangeSelected = obj;
  };

  @action
  setSelectionReset = (value = false) => {
    this.isSelectionReset = value;
  };

  @action
  setIsFromSeoPage = value => {
    this.isFromSeoPage = value;
  };

  @action
  setFoundData = value => {
    this.foundData = value;
  };

  @action
  setIsEmailViewedFromSeoPage = value => {
    this.isEmailViewedFromSeoPage = value;
  };

  @action
  setDiscoverTableLoading = val => {
    this.discoverTableLoading = val;
  };

  @action
  setIsFindPhoneEmailClicked = val => {
    this.isFindPhoneEmailClicked = val;
  };

  @action
  setFindDataType = val => {
    this.findDataType = val;
  };

  @action
  setSinglePurchaseContactName = val => {
    this.singlePurchaseContactName = val;
  };

  @action
  setSinglePurchaseContactMail = val => {
    this.singlePurchaseContactMail = val;
  };

  @action
  setHasData = val => {
    this.hasData = val;
  };

  @action
  setContactId = val => {
    this.contactId = val;
  };

  @action
  setMobilePhone = val => {
    this.mobilePhone = val;
  };

  @action
  setIsMobileNumberNotFound = val => {
    this.isMobileNumberNotFound = val;
  };

  @action
  setIsPhonePurchase = val => {
    this.isPhonePurchase = val;
  };

  @action
  setIsNoPhoneCredits = val => {
    this.isNoPhoneCredits = val;
  };

  @action
  setTotalEnrichContactsCount = val => {
    this.totalEnrichContactsCount = val;
  };

  @action
  setContactCompanyName = val => {
    this.contactCompanyName = val;
  };

  @action
  setFoundMaskedNumber = val => {
    this.foundMaskedNumber = val;
  };

  @action
  closePopup = () => {
    dataSearchState.setSinglePurchaseContactMail("");
    dataSearchState.setMobilePhone("");
    dataSearchState.setContactId("");
    dataSearchState.setSinglePurchaseContactName("");
    dataSearchState.setIsMobileNumberNotFound(true);
    dataSearchState.setIsPhonePurchase(false);
    dataSearchState.setFindDataType("");
    dataSearchState.setContactCompanyName("");
    dataSearchState.setFoundMaskedNumber(false);
  };

  hasAnyFilterSelected = () => {
    const d = this.filterComponentData || {};
    const skip = new Set([
      "excludeTeamOwns", "titleExactMatch", "primaryActiveRoleOnly", "companySearchType", "contactSearchType",
      "country", "state", "city", "excludeContactsMode", "excludeContactListId", "excludeContactListName",
      "companyCountry", "companyState", "companyCity"
    ]);
    const experienceKeys = [
      "experienceCurrentJobMinYear",
      "experienceCurrentJobMaxYear",
      "experienceCurrentJobMinMonth",
      "experienceCurrentJobMaxMonth",
      "experienceCurrentCompanyMinYear",
      "experienceCurrentCompanyMaxYear",
      "experienceCurrentCompanyMinMonth",
      "experienceCurrentCompanyMaxMonth",
      "experienceTotalMinYear",
      "experienceTotalMaxYear",
      "experienceTotalMinMonth",
      "experienceTotalMaxMonth"
    ];
    const hasValue = (k, v) => {
      if (experienceKeys.includes(k)) {
        return (Number(v) || 0) > 0;
      }
      if (k === "employeesByDepartment") {
        return (
          Array.isArray(v) &&
          v.some(
            row =>
              (row.function || []).length > 0 &&
              (row.start != null || row.end != null)
          )
        );
      }
      if (k === "growthByDepartment") {
        return (
          Array.isArray(v) &&
          v.some(
            row =>
              (row.function || []).length > 0 &&
              (row.start != null || row.end != null || !!row.timeFrame)
          )
        );
      }
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      if (typeof v === "boolean") return false;
      if (typeof v === "number") return v !== 0;
      return v !== undefined && v !== null;
    };
    const hasFilterData = Object.entries(d).some(([k, v]) => !skip.has(k) && hasValue(k, v));
    if (hasFilterData) return true;
    const sl = this.selectedFilteredList || {};
    if ((sl.location || []).length > 0) return true;
    if ((sl.companyLocation || []).length > 0) return true;
    return false;
  };

  @action
  setExperienceDurationErrors = val => {
    this.experienceDurationErrors = typeof val === "object" && val !== null ? val : {};
  };

  @action
  setEmployeesByDepartmentErrors = val => {
    this.employeesByDepartmentErrors = typeof val === "object" && val !== null ? val : {};
  };

  @action
  setGrowthByDepartmentErrors = val => {
    this.growthByDepartmentErrors = typeof val === "object" && val !== null ? val : {};
  };

  @action
  setExcludeContactsLoading = val => {
    this.excludeContactsLoading = !!val;
  };

  @action
  setActiveSearchTab = val => {
    this.activeSearchTab = val;
  };

  @action
  setCompanyTableData = val => {
    this.companyTableData = val;
  };

  @action
  setCompanyCurrentPage = val => {
    this.companyCurrentPage = val;
  };

  @action
  setCompanyTableLoading = val => {
    this.companyTableLoading = val;
  };

  @action
  setCompanyShowInitialPage = val => {
    this.companyShowInitialPage = val;
  };

  @action
  setCompanyLastSearchedFilters = val => {
    this.companyLastSearchedFilters = val;
  };

  getCompanyFiltersDirtySinceLastSearch = () => {
    const prev = this.companyLastSearchedFilters;
    if (prev == null) return false;
    const curr = this.filterComponentData;
    const keys = new Set([...Object.keys(prev), ...Object.keys(curr)]);
    for (const k of keys) {
      if (JSON.stringify(prev[k]) !== JSON.stringify(curr[k])) return true;
    }
    return false;
  };

  @action
  searchCompanyData = async () => {
    if (this.excludeContactsLoading) {
      toasterState.setToastMsg("Please wait until exclude contacts list finishes loading.", "fail");
      return false;
    }
    if (!this.hasAnyFilterSelected()) {
      toasterState.setToastMsg("Please select at least one filter to search.", "fail");
      return false;
    }
    this.setCompanyTableLoading(true);
    const fd = this.filterComponentData;
    const searchType = fd.companySearchType || "domain";

    let companyDomain = [];
    let companyName = [];
    let companyLinkedIn = [];
    if (searchType === "domain") {
      companyDomain = fd.companyDomain || [];
    } else if (searchType === "domainList") {
      companyDomain = fd.companyDomainList || [];
    } else if (searchType === "nameList") {
      companyName = fd.companyNameList || [];
    } else if (searchType === "name") {
      companyName = fd.companyName || [];
    } else if (searchType === "linkedin") {
      companyLinkedIn = fd.companyLinkedIn || [];
    } else if (searchType === "linkedinList") {
      companyLinkedIn = fd.companyLinkedInList || [];
    }

    let employeesByDepartment = fd.employeesByDepartment || [];
    if (employeesByDepartment.length > 0) {
      employeesByDepartment = employeesByDepartment.map(row => {
        const out = { function: row.function || [] };
        if (row.start != null) out.start = row.start;
        if (row.end != null && row.end > 0) out.end = row.end;
        return out;
      });
    }
    let growthByDepartment = fd.growthByDepartment || [];
    if (growthByDepartment.length > 0) {
      growthByDepartment = growthByDepartment
        .map(row => {
          const out = { function: row.function || [] };
          if (row.start != null) out.start = row.start;
          if (row.end != null && row.end > 0) out.end = row.end;
          if (row.timeFrame) out.timeFrame = row.timeFrame;
          return out;
        })
        .filter(row => (row.function || []).length > 0 && (row.start != null || row.end != null || row.timeFrame));
    }

    const payload = {
      companyDomain,
      companyName,
      companyLinkedIn,
      companyDomainList: [],
      companyNameList: [],
      companyLinkedInList: [],
      companyCountry: fd.companyCountry || [],
      companyState: fd.companyState || [],
      companyCity: fd.companyCity || [],
      industry: fd.industry || [],
      industryExclude: fd.industryExclude || [],
      employeeCount: fd.employeeCount || [],
      revenue: fd.revenue || [],
      companyType: fd.companyType || [],
      foundedYearStart: fd.foundedYearStart || null,
      foundedYearEnd: fd.foundedYearEnd || null,
      companyKeywords: fd.companyKeywords || [],
      companyKeywordsExclude: fd.companyKeywordsExclude || [],
      technologies: fd.technologies || [],
      funding: fd.funding || [],
      fundingTotalAmountStart: fd.fundingTotalAmountStart || null,
      fundingTotalAmountEnd: fd.fundingTotalAmountEnd || null,
      fundingLastAmountStart: fd.fundingLastAmountStart || null,
      fundingLastAmountEnd: fd.fundingLastAmountEnd || null,
      fundingDurationStart: fd.fundingDurationStart || null,
      fundingDurationEnd: fd.fundingDurationEnd || null,
      employeesByDepartment,
      growthByDepartment,
      limit: this.pageLimit,
      pageNo: this.companyCurrentPage
    };

    checkAndCancelRequest();
    this.setCompanyShowInitialPage(false);
    const config = {
      url: URLS.searchCompaniesV2,
      method: "POST",
      data: payload
    };
    const res = await makeApi(config, cancelApiRequest);
    if (
      res?.message?.message?.toLowerCase() === "cancel" ||
      res?.message?.toLowerCase() === "cancel"
    ) {
      console.error("Company search request cancelled");
    } else {
      this.setCompanyTableData(res?.data || {});
      this.setCompanyLastSearchedFilters(
        JSON.parse(JSON.stringify(this.filterComponentData))
      );
    }
    this.setCompanyTableLoading(false);
    return true;
  };

  @action
  searchPageData = async (storePrevValue = false) => {
    if (this.excludeContactsLoading) {
      toasterState.setToastMsg("Please wait until exclude contacts list finishes loading.", "fail");
      this.discoverTableLoading = false;
      return false;
    }
    if (!this.hasAnyFilterSelected()) {
      toasterState.setToastMsg("Please select at least one filter to search.", "fail");
      this.discoverTableLoading = false;
      return false;
    }
    const hasExperienceError = Object.values(this.experienceDurationErrors || {}).some(Boolean);
    if (hasExperienceError) {
      toasterState.setToastMsg("Fix the errors in the Experience filter before searching.", "fail");
      this.discoverTableLoading = false;
      return false;
    }
    const hasEmployeesByDeptError = Object.values(this.employeesByDepartmentErrors || {}).some(Boolean);
    if (hasEmployeesByDeptError) {
      toasterState.setToastMsg("Max must be greater than or equal to Min in Employees by Department.", "fail");
      this.discoverTableLoading = false;
      return false;
    }
    const hasGrowthByDeptError = Object.values(this.growthByDepartmentErrors || {}).some(Boolean);
    if (hasGrowthByDeptError) {
      toasterState.setToastMsg("Fix the errors in the Growth by Department filter before searching.", "fail");
      this.discoverTableLoading = false;
      return false;
    }
    const lbSearchesEligible = userDetail?.userFeatureFlag?.lbSearchesEligible ?? 0;
    const lbSearchesUsed = userDetail?.userFeatureFlag?.lbSearchesUsed ?? 0;
    const lbSearchesRemaining = lbSearchesEligible - lbSearchesUsed;
    if (lbSearchesRemaining <= 0) {
      upgradePopupState.setPopupValues({ type: "lbSearchLimitReached" });
      upgradePopupState.setPopup(true);
      this.discoverTableLoading = false;
      return false;
    }
    this.setDiscoverTableLoading(true);
    let pageNo = this.currentPage;
    const totalContacts = this.contactTableData?.totalContacts ?? 0;
    if (totalContacts > DISCOVER_TOTAL_COUNT_CAP) {
      const maxPage = Math.ceil(DISCOVER_TOTAL_COUNT_CAP / this.pageLimit);
      pageNo = Math.min(pageNo, maxPage);
    }
    const searchType = this.filterComponentData.companySearchType || "domain";
    const payload = { ...this.filterComponentData };
    payload.companyDomain = [];
    payload.companyName = [];
    payload.companyLinkedIn = [];
    payload.companyDomainList = [];
    payload.companyNameList = [];
    payload.companyLinkedInList = [];
    if (searchType === "domain") {
      payload.companyDomain = this.filterComponentData.companyDomain || [];
    } else if (searchType === "domainList") {
      payload.companyDomain = this.filterComponentData.companyDomainList || [];
    } else if (searchType === "nameList") {
      payload.companyName = this.filterComponentData.companyNameList || [];
    } else if (searchType === "name") {
      payload.companyName = this.filterComponentData.companyName || [];
    } else if (searchType === "linkedin") {
      payload.companyLinkedIn = this.filterComponentData.companyLinkedIn || [];
    } else if (searchType === "linkedinList") {
      payload.companyLinkedIn = this.filterComponentData.companyLinkedInList || [];
    }
    const contactSt = this.filterComponentData.contactSearchType || "name";
    payload.name = [];
    payload.contactLinkedIn = [];
    if (contactSt === "name") {
      const raw = this.filterComponentData.name;
      payload.name = Array.isArray(raw) ? [...raw] : typeof raw === "string" && raw.trim() ? [raw.trim()] : [];
    } else if (contactSt === "linkedin") {
      payload.contactLinkedIn = this.filterComponentData.contactLinkedIn || [];
    }
    // Only send start/end when set; never send end when Max is empty (omit key)
    if (Array.isArray(payload.employeesByDepartment) && payload.employeesByDepartment.length > 0) {
      payload.employeesByDepartment = payload.employeesByDepartment.map(row => {
        const out = { function: row.function || [] };
        if (row.start != null) out.start = row.start;
        if (row.end != null && row.end > 0) out.end = row.end;
        return out;
      });
    }
    // Growth by Department: map to function/start/end/timeFrame, omitting empty values
    if (Array.isArray(payload.growthByDepartment) && payload.growthByDepartment.length > 0) {
      payload.growthByDepartment = payload.growthByDepartment
        .map(row => {
          const out = { function: row.function || [] };
          if (row.start != null) out.start = row.start;
          if (row.end != null && row.end > 0) out.end = row.end;
          if (row.timeFrame) out.timeFrame = row.timeFrame;
          // Require at least one constraint besides function
          return out;
        })
        .filter(row => (row.function || []).length > 0 && (row.start != null || row.end != null || row.timeFrame));
    }
    const config = {
      url: URLS.searchDataPageV2,
      method: "POST",
      data: {
        ...payload,
        limit: this.pageLimit,
        pageNo
      }
    };
    checkAndCancelRequest();
    dataSearchState.setShowInitialPage(false);
    let res = await makeApi(config, cancelApiRequest);
    if (
      res?.message?.message?.toLowerCase() === "cancel" ||
      res?.message?.toLowerCase() === "cancel"
    ) {
      Utils.mixpanelTrack(MXP_EVENT.SEARCH_PERFORMED, {
        pageType: PAGE_TYPE.discover,
        searchCriteria: config?.data || {},
        results: res?.data || {},
        Network: "Request cancelled"
      });
      console.error("Request cancelled");
    } else {
      if (storePrevValue) {
        const updatedResData = [
          ...this.contactTableData?.contacts,
          ...res?.data?.contacts
        ];
        this.setIsFromSeoPage(false);
        this.setContactTableData({
          ...res?.data,
          contacts: updatedResData,
          totalContacts: res?.data?.totalContacts + 1
        });
        this.mergeOwnedContactData(updatedResData);
      } else {
        this.setContactTableData(res?.data || {});
        this.mergeOwnedContactData(res?.data?.contacts);
      }
      Utils.mixpanelTrack(MXP_EVENT.SEARCH_PERFORMED, {
        pageType: PAGE_TYPE.discover,
        searchCriteria: config?.data || {},
        results: res?.data || {}
      });
    }
    if (+res.status === 200) {
      this.lastSearchedFilters = JSON.parse(JSON.stringify(this.filterComponentData));
      this.discoverTableLoading = false;
      if (!storePrevValue) {
        try {
          await makeApi({
            url: URLS.incrementLbSearchesUsed,
            method: "POST"
          });
          const flags = await getFeatureFlagWithTeam(false);
          if (flags && typeof flags.lbSearchesUsed === "number") {
            userDetail.setUserFeatureFlag({ ...userDetail.userFeatureFlag, lbSearchesUsed: flags.lbSearchesUsed });
          }
        } catch (err) {
          console.error("Failed to increment lbSearchesUsed", err);
        }
      }
    }
    return true;
  };

  /**
   * Find email via LinkedIn flow (POST /cm/tp/contact) for a single contact from Discover v2.
   * Uses firstName, lastName, linkedIn, title, companyDomain from the row so backend can skip B2B enrich.
   */
  @action
  purchaseSingleContactFindEmail = async (contact, contactIdForRow) => {
    const linkedInUrl =
      contact?.linkedIn ?? contact?.linkedin ?? contact?.linkedInProfileUrl ?? "";
    if (!linkedInUrl || !linkedInUrl.trim()) {
      toasterState.setToastMsg("LinkedIn URL is required to find email", "fail");
      return;
    }
    const firstName = contact?.firstName ?? "";
    const lastName = contact?.lastName ?? "";
    const fullName = ([firstName, lastName].filter(Boolean).join(" ") || contact?.fullName) ?? "";
    let domain =
      contact?.companyDomain ??
      contact?.primaryDomain ??
      contact?.companyWebsite ??
      "";
    if (typeof domain === "string" && domain.trim()) {
      domain = domain.trim().replace(/^https?:\/\//i, "").split("/")[0] || domain.trim();
    }
    const experience = [];
    if (contact?.title || contact?.companyName || domain) {
      experience.push({
        title: contact.title ?? "",
        companyName: contact.companyName ?? "",
        domain: typeof domain === "string" ? domain : ""
      });
    }
    const payload = {
      type: "EMAIL",
      linkedInProfileUrl: linkedInUrl.trim(),
      fullName: fullName || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      experience: experience.length > 0 ? experience : undefined,
      city: contact?.city ?? undefined,
      state: contact?.state ?? undefined,
      country: contact?.country ?? undefined,
      companyLinkedIn: contact?.companyLinkedIn ?? undefined
    };
    try {
      const res = await makeApi({
        url: URLS.findEmailFromLinkedIn,
        method: "POST",
        data: payload
      });
      const data = res?.data ?? res?.response?.data ?? {};
      const httpStatus = res?.status ?? res?.response?.status;
      const responseCode = data.responseCode ?? data.statusCode ?? httpStatus;
      const contactRes = data.contact ?? {};
      const email = contactRes.email ?? "";
      const creditsUsed = data.creditsUsed ?? 0;
      const responseText = (
        data?.invalidMessage ||
        data?.message ||
        res?.message ||
        ""
      )
        .toString()
        .toLowerCase();
      const isNotFoundLikeResponse =
        responseCode === 208 ||
        responseCode === 404 ||
        httpStatus === 404 ||
        responseText.includes("not found") ||
        responseText.includes("unavailable") ||
        responseText.includes("no email");

      if (responseCode === 201 || responseCode === 202 || responseCode === 200) {
        if (email) {
          const cloned = Utils.createObjWithoutRef(this.contactTableData) ?? {};
          const contacts = cloned?.contacts ?? [];
          const updated = contacts.map(c =>
            c?.id === contactIdForRow
              ? {
                  ...c,
                  email,
                  emailOwned: true,
                  hasShow: true
                }
              : c
          );
          this.setContactTableData({ ...cloned, contacts: updated });
          this.setSinglePurchaseContactMail(email);
          toasterState.setToastMsg(
            creditsUsed > 0
              ? `Email found! ${creditsUsed} credit deducted.`
              : "Email found! No credits deducted.",
            "success"
          );
          try {
            const flags = await getFeatureFlagWithTeam();
            if (flags) userDetail.setUserFeatureFlag(flags);
          } catch (_) {}
        } else {
          toasterState.setToastMsg("Email not found for this contact", "fail");
        }
        Utils.mixpanelTrack(MXP_EVENT["VIEW_EMAIL_SINGLE_PERFORMED"], {
          pageType: PAGE_TYPE.discover,
          success: !!email
        });
      } else if (responseCode === 204) {
        toasterState.setToastMsg(
          "You've used all your credits this month. Upgrade to reveal more emails.",
          "fail"
        );
      } else if (isNotFoundLikeResponse) {
        toasterState.setToastMsg("Email not found for this contact", "fail");
      } else {
        const msg =
          data?.invalidMessage ?? data?.message ?? "Something went wrong. Please try again.";
        toasterState.setToastMsg(msg, "fail");
      }
    } catch (err) {
      const errText = (
        err?.response?.data?.invalidMessage ??
        err?.response?.data?.message ??
        err?.message ??
        ""
      )
        .toString()
        .toLowerCase();
      if (
        errText.includes("not found") ||
        errText.includes("unavailable") ||
        errText.includes("no email")
      ) {
        toasterState.setToastMsg("Email not found for this contact", "fail");
        Utils.mixpanelTrack(MXP_EVENT["VIEW_EMAIL_SINGLE_PERFORMED"], {
          pageType: PAGE_TYPE.discover,
          success: false
        });
        return;
      }
      const msg =
        err?.response?.data?.invalidMessage ??
        err?.response?.data?.message ??
        err?.message ??
        "Failed to find email. Please try again.";
      toasterState.setToastMsg(msg, "fail");
      Utils.mixpanelTrack(MXP_EVENT["VIEW_EMAIL_SINGLE_PERFORMED"], {
        pageType: PAGE_TYPE.discover,
        success: false
      });
    }
  };

  /**
   * Find phone only via LinkedIn (POST /cm/tp/contact, type PHONE). Uses only LinkedIn profile URL;
   * no email lookup, no B2B enrich. Backend calls Prospeo/Wiza with LinkedIn URL and returns phone.
   */
  @action
  purchaseSingleContactFindPhone = async (contact, contactIdForRow) => {
    const linkedInUrl =
      contact?.linkedIn ?? contact?.linkedin ?? contact?.linkedInProfileUrl ?? "";
    if (!linkedInUrl || !linkedInUrl.trim()) {
      toasterState.setToastMsg("LinkedIn URL is required to find phone", "fail");
      return;
    }
    const payload = {
      type: "PHONE",
      linkedInProfileUrl: linkedInUrl.trim(),
      firstName: contact?.firstName || undefined,
      lastName: contact?.lastName || undefined,
      city: contact?.city ?? undefined,
      state: contact?.state ?? undefined,
      country: contact?.country ?? undefined,
      companyLinkedIn: contact?.companyLinkedIn ?? undefined
    };
    try {
      const res = await makeApi({
        url: URLS.findEmailFromLinkedIn,
        method: "POST",
        data: payload
      });
      const data = res?.data ?? {};
      const responseCode = data.responseCode ?? data.statusCode;
      const contactRes = data.contact ?? {};
      const phone = contactRes.mobilePhone ?? "";
      const email = contactRes.email ?? "";
      const creditsUsed = data.creditsUsed ?? 0;

      if (responseCode === 201 || responseCode === 202 || responseCode === 200) {
        if (phone || email) {
          const cloned = Utils.createObjWithoutRef(this.contactTableData) ?? {};
          const contacts = cloned?.contacts ?? [];
          const updated = contacts.map(c => {
            if (c?.id !== contactIdForRow) return c;
            const updates = { hasShow: true };
            if (phone) {
              updates.phoneOwned = true;
              updates.mobilePhone = [{ number: phone }];
            }
            if (email) {
              updates.email = email;
              updates.emailOwned = true;
            }
            return { ...c, ...updates };
          });
          this.setContactTableData({ ...cloned, contacts: updated });
          if (email) this.setSinglePurchaseContactMail(email);
          toasterState.setToastMsg(
            creditsUsed > 0
              ? `Contact details found! ${creditsUsed} credit deducted.`
              : "Contact details found! No credits deducted.",
            "success"
          );
          try {
            const flags = await getFeatureFlagWithTeam();
            if (flags) userDetail.setUserFeatureFlag(flags);
          } catch (_) {}
        } else {
          toasterState.setToastMsg("Phone number not found for this contact", "fail");
        }
        Utils.mixpanelTrack(MXP_EVENT.FIND_PHONE_NUMBER_CLICKED, {
          pageType: PAGE_TYPE.discover,
          success: !!phone
        });
      } else if (responseCode === 204) {
        toasterState.setToastMsg(
          "You've used all your credits this month. Upgrade to reveal more contacts.",
          "fail"
        );
      } else if (responseCode === 208 || responseCode === 404) {
        toasterState.setToastMsg("Phone number not found for this contact", "fail");
      } else {
        const msg =
          data?.invalidMessage ?? data?.message ?? "Something went wrong. Please try again.";
        toasterState.setToastMsg(msg, "fail");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.invalidMessage ??
        err?.response?.data?.message ??
        err?.message ??
        "Failed to find phone. Please try again.";
      toasterState.setToastMsg(msg, "fail");
      Utils.mixpanelTrack(MXP_EVENT.FIND_PHONE_NUMBER_CLICKED, {
        pageType: PAGE_TYPE.discover,
        success: false
      });
    }
  };

  @action
  purchaseSingleContact = async (data = {}, history = []) => {
    dataSearchState.setIsFindPhoneEmailClicked(true);
    data?.phonePurchase
      ? dataSearchState.setIsPhonePurchase(true)
      : dataSearchState.setIsPhonePurchase(false);
    const config = {
      url: URLS.purchaseContact,
      method: "POST",
      data
    };
    let res = await makeApi(config);
    if (res?.status === 200) {
      if (this.isFromSeoPage) {
        this.setIsEmailViewedFromSeoPage(true);
        dataSearchState.setIsFromSeoPage(true);
        setTimeout(() => {
          window.Intercom("startTour", IntercomTour.viewEmailFromSeoPage);
        }, 2000);
      }
      handlePurchaseResponse(res?.data, "", history);
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
    if (data?.emailPurchase) {
      Utils.mixpanelTrack(MXP_EVENT["VIEW_EMAIL_SINGLE_PERFORMED"], {
        pageType: PAGE_TYPE.discover,
        success: res?.status === 200
      });
    }
  };

  @action
  purchaseBulkContact = async (purchaseType, history) => {
    const config = {
      url: URLS.purchaseContact,
      method: "POST",
      data: this.getBulkPurchaseContactPayload(purchaseType)
    };
    let res = await makeApi(config);
    if (res?.status === 200) {
      if (this.isFromSeoPage) {
        this.setIsEmailViewedFromSeoPage(true);
        dataSearchState.setIsFromSeoPage(true);
        if (res?.data?.contacts?.length > 0) {
          res.data.contacts[0].isSelected = true;
        }
      }
      handlePurchaseResponse(res?.data, "", history);
    } else {
      popupLoaderState.setShowPopup(false);
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
    Utils.mixpanelTrack(
      MXP_EVENT[
      config?.data?.emailPurchase
        ? "VIEW_EMAIL_BULK_PERFORMED"
        : "VIEW_EMAIL_PHONE_BULK_PERFORMED"
      ],
      {
        pageType: PAGE_TYPE.discover,
        success: res?.status === 200,
        searchCriteria: config?.data || {}
      }
    );
  };

  @action
  addContactToList = async list => {
    const { planName = "", inFreePlan = false } = userDetail?.userFeatureFlag || {};
    const selectedIds = this.selectedContactData || [];
    const pageContacts = this.contactTableData?.contacts || [];
    const rangeSelected = this.rangeSelected || {};
    const limitVal = rangeSelected?.limit;
    const limitNum =
      typeof limitVal === "number"
        ? limitVal
        : typeof limitVal === "string"
          ? parseInt(limitVal, 10)
          : 0;
    const isCustomOrBulkRange =
      (rangeSelected?.type === "custom" || rangeSelected?.type === "bulk") &&
      !Number.isNaN(limitNum) &&
      limitNum > 0;
    const effectiveCount = isCustomOrBulkRange ? limitNum : selectedIds.length;

    if (!effectiveCount) {
      toasterState.setToastMsg("No contacts selected", "fail");
      return;
    }

    const BULK_THRESHOLD = 25;
    const BULK_MAX_LIMIT = 10000;
    // Only "Select top N" / "Custom selection" (range) selections should use the bulk
    // background job, which re-runs the search from page 0 with a limit. Explicit per-page
    // selections (Select all on this page / individual checkboxes) must add exactly the
    // contacts the user picked on the current page, regardless of count — otherwise paging
    // to a later page and adding would still add the first N results (page 1 contacts).
    if (isCustomOrBulkRange && effectiveCount > BULK_THRESHOLD) {
      // Bulk path: background job calls AI-Ark once with limit (max 10,000), then adds all to list.
      const limit = Math.min(effectiveCount, BULK_MAX_LIMIT);
      popupLoaderState.setData({
        message: `Adding ${limit} contacts to your list. Please wait...`,
        type: "bulkUpload",
        speed: 5
      });
      popupLoaderState.setShowPopup(true);
      try {
        // Normalize company filters based on companySearchType, same as searchPageData does,
        // so the backend receives the correct companyDomain/companyName fields.
        const bulkSearchType = this.filterComponentData.companySearchType || "domain";
        const normalizedFilters = { ...this.filterComponentData };
        normalizedFilters.companyDomain = [];
        normalizedFilters.companyName = [];
        normalizedFilters.companyLinkedIn = [];
        normalizedFilters.companyDomainList = [];
        normalizedFilters.companyNameList = [];
        normalizedFilters.companyLinkedInList = [];
        if (bulkSearchType === "domain") {
          normalizedFilters.companyDomain = this.filterComponentData.companyDomain || [];
        } else if (bulkSearchType === "domainList") {
          normalizedFilters.companyDomain = this.filterComponentData.companyDomainList || [];
        } else if (bulkSearchType === "nameList") {
          normalizedFilters.companyName = this.filterComponentData.companyNameList || [];
        } else if (bulkSearchType === "name") {
          normalizedFilters.companyName = this.filterComponentData.companyName || [];
        } else if (bulkSearchType === "linkedin") {
          normalizedFilters.companyLinkedIn = this.filterComponentData.companyLinkedIn || [];
        } else if (bulkSearchType === "linkedinList") {
          normalizedFilters.companyLinkedIn = this.filterComponentData.companyLinkedInList || [];
        }
        const contactSt = this.filterComponentData.contactSearchType || "name";
        normalizedFilters.name = [];
        normalizedFilters.contactLinkedIn = [];
        if (contactSt === "name") {
          const raw = this.filterComponentData.name;
          normalizedFilters.name = Array.isArray(raw) ? [...raw] : typeof raw === "string" && raw.trim() ? [raw.trim()] : [];
        } else if (contactSt === "linkedin") {
          normalizedFilters.contactLinkedIn = this.filterComponentData.contactLinkedIn || [];
        }
        const res = await makeApi({
          url: URLS.bulkAddFromDiscoverV2,
          method: "POST",
          data: {
            listId: list?.id ?? list?._id ?? null,
            limit,
            filterComponentData: normalizedFilters
          }
        });
        popupLoaderState.setShowPopup(false);
        // makeApi returns the error object on 4xx instead of throwing
        if (res?.response != null) {
          const status = res.response.status;
          const data = res.response.data || {};
          const dataMsg = data?.invalidMessage || data?.message || "";
          if (status === 402 || status === "402" || (dataMsg && /not enough credits|please upgrade to add more contacts/i.test(String(dataMsg)))) {
            PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
            throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
          }
          toasterState.setToastMsg(
            dataMsg || "Failed to start adding contacts to list. Please try again.",
            "fail"
          );
          Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
            pageType: PAGE_TYPE.discover,
            listName: list?.name,
            searchCriteria: { limit, listId: list?.id },
            success: false
          });
          return;
        }
        const body = res?.data || {};
        const bulkResponseCode = body?.responseCode;
        const bulkMsg = body?.invalidMessage || body?.message || "";
        if (
          bulkResponseCode === 402 ||
          bulkResponseCode === "402" ||
          (bulkMsg && /not enough credits|please upgrade to add more contacts/i.test(String(bulkMsg)))
        ) {
          PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
          throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
        }
        const ok = res?.status === 200 && (body.background === true || body.responseCode === 200);
        if (ok && body.background) {
          customListState.getContactList();
          this.setIsFromSeoPage(false);
          this.setSelectedContactData([]);
          bulkJobsNotificationStore.fetchJobs();
          bulkJobsNotificationStore.triggerGuide(
            "Your add-to-list job has started. Track it here."
          );
        } else {
          toasterState.setToastMsg(
            body?.invalidMessage || "Failed to start adding contacts to list. Please try again.",
            "fail"
          );
        }
        Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
          pageType: PAGE_TYPE.discover,
          listName: list?.name,
          searchCriteria: { limit, listId: list?.id },
          success: ok
        });
      } catch (err) {
        popupLoaderState.setShowPopup(false);
        if (err?.code === ADD_TO_LIST_UPGRADE_REQUIRED) {
          throw err;
        }
        if (err?.response?.status === 402) {
          PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
          throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
        }
        const bulkErrMsg =
          err?.response?.data?.invalidMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "";
        if (err?.response?.status === 402 || err?.response?.status === "402" || (bulkErrMsg && /not enough credits|please upgrade to add more contacts/i.test(String(bulkErrMsg)))) {
          PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
          throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
        }
        toasterState.setToastMsg(
          bulkErrMsg || "Failed to add contacts to list. Please try again.",
          "fail"
        );
        Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
          pageType: PAGE_TYPE.discover,
          listName: list?.name,
          searchCriteria: {},
          success: false
        });
      }
      return;
    }

    let contactsToAdd = [];
    const allSelectedOnPage =
      !isCustomOrBulkRange &&
      selectedIds.length <= pageContacts.length &&
      selectedIds.every(id => pageContacts.some(c => c?.id === id));

    if (allSelectedOnPage) {
      contactsToAdd = pageContacts.filter(c => selectedIds.includes(c?.id));
    } else {
      // Custom/bulk: fetch by range limit; otherwise fetch by selected IDs count.
      const fetchLimit = isCustomOrBulkRange ? limitNum : selectedIds.length;
      try {
        // Build the same normalized payload that searchPageData uses so filters are applied correctly.
        const searchType = this.filterComponentData.companySearchType || "domain";
        const fetchPayload = { ...this.filterComponentData };
        fetchPayload.companyDomain = [];
        fetchPayload.companyName = [];
        fetchPayload.companyLinkedIn = [];
        fetchPayload.companyDomainList = [];
        fetchPayload.companyNameList = [];
        fetchPayload.companyLinkedInList = [];
        if (searchType === "domain") {
          fetchPayload.companyDomain = this.filterComponentData.companyDomain || [];
        } else if (searchType === "domainList") {
          fetchPayload.companyDomain = this.filterComponentData.companyDomainList || [];
        } else if (searchType === "nameList") {
          fetchPayload.companyName = this.filterComponentData.companyNameList || [];
        } else if (searchType === "name") {
          fetchPayload.companyName = this.filterComponentData.companyName || [];
        } else if (searchType === "linkedin") {
          fetchPayload.companyLinkedIn = this.filterComponentData.companyLinkedIn || [];
        } else if (searchType === "linkedinList") {
          fetchPayload.companyLinkedIn = this.filterComponentData.companyLinkedInList || [];
        }
        const contactSt = this.filterComponentData.contactSearchType || "name";
        fetchPayload.name = [];
        fetchPayload.contactLinkedIn = [];
        if (contactSt === "name") {
          const raw = this.filterComponentData.name;
          fetchPayload.name = Array.isArray(raw) ? [...raw] : typeof raw === "string" && raw.trim() ? [raw.trim()] : [];
        } else if (contactSt === "linkedin") {
          fetchPayload.contactLinkedIn = this.filterComponentData.contactLinkedIn || [];
        }
        const discoverRes = await makeApi({
          url: URLS.searchDataPageV2,
          method: "POST",
          data: {
            ...fetchPayload,
            size: fetchLimit,
            page: 0
          }
        });
        contactsToAdd = discoverRes?.data?.contacts || [];
      } catch (e) {
        toasterState.setToastMsg("Failed to load contacts. Please try again.", "fail");
        return;
      }
    }

    const payload = this.buildAddToDirectoryPayload(contactsToAdd, list);
    if (!payload?.contacts?.length) {
      toasterState.setToastMsg("No valid contacts to add (need LinkedIn URL)", "fail");
      return;
    }

    const config = {
      url: URLS.addContactsToDirectory,
      method: "POST",
      data: payload
    };
    let res;
    try {
      res = await makeApi(config);
    } catch (err) {
      if (err?.code === ADD_TO_LIST_UPGRADE_REQUIRED) throw err;
      const status = err?.response?.status;
      const errMsg = err?.response?.data?.invalidMessage || err?.response?.data?.message || err?.message;
      if (status === 402 || status === "402" || (errMsg && /not enough credits|please upgrade to add more contacts/i.test(String(errMsg)))) {
        PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
        throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
      }
      toasterState.setToastMsg(
        errMsg || (status === 401 ? "Please sign in again." : "Failed to add contacts to list. Please try again."),
        "fail"
      );
      Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
        pageType: PAGE_TYPE.discover,
        listName: list?.name,
        searchCriteria: config?.data || {},
        success: false
      });
      return;
    }

    // makeApi returns the error object on 4xx instead of throwing – treat as error when res.response exists
    const isErrorResponse = res?.response != null;
    const body = isErrorResponse ? res?.response?.data || {} : res?.data || {};
    const responseCode = body?.responseCode;
    const httpStatus = isErrorResponse ? res?.response?.status : res?.status;
    const invalidMsg = body?.invalidMessage || body?.message || "";

    // Show upgrade popup for 402 (insufficient credits) – by status, responseCode, or message content
    const isUpgradeRequired =
      httpStatus === 402 ||
      httpStatus === "402" ||
      responseCode === 402 ||
      responseCode === "402" ||
      /not enough credits|please upgrade to add more contacts/i.test(String(invalidMsg));
    if (isUpgradeRequired) {
      PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
      throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
    }

    if (isErrorResponse) {
      const data = body;
      const msg = data.invalidMessage || data.message || res.message;
      if (msg && /not enough credits|please upgrade to add more contacts/i.test(String(msg))) {
        PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
        throw { code: ADD_TO_LIST_UPGRADE_REQUIRED };
      }
      toasterState.setToastMsg(
        msg || (httpStatus === 401 ? "Please sign in again." : "Failed to add contacts to list. Please try again."),
        "fail"
      );
      Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
        pageType: PAGE_TYPE.discover,
        listName: list?.name,
        searchCriteria: config?.data || {},
        success: false
      });
      return;
    }

    const ok = res?.status === 200;
    const validContacts = body.validContacts ?? 0;
    const invalidContacts = body.invalidContacts ?? 0;
    const invalidMessage = body.invalidMessage;
    const success = ok || responseCode === 200;
    if (success) {
      customListState.getContactList();
      this.setIsFromSeoPage(false);
      this.setSelectedContactData([]);
      handlePurchaseResponse(
        { addContactToListResponse: { successCount: validContacts }, creditsResponse: {} },
        list?.name
      );
      if (validContacts > 0) {
        toasterState.setToastMsg(
          `${validContacts} contact${validContacts !== 1 ? "s" : ""} added to ${list?.name || "list"}`,
          "success"
        );
      } else if (invalidContacts === 0) {
        toasterState.setToastMsg(`Added to ${list?.name || "list"}`, "success");
      }
      if (invalidContacts > 0) {
        const reason = invalidMessage || "duplicate or invalid";
        toasterState.setToastMsg(
          `${invalidContacts} contact${invalidContacts !== 1 ? "s" : ""} could not be added: ${reason}`,
          "fail"
        );
      }
    } else {
      // 402 should already be handled above; avoid showing toaster for upgrade-required
      if (responseCode !== 402 && responseCode !== "402") {
        toasterState.setToastMsg(
          invalidMessage || "Something went wrong. Please try again later",
          "fail"
        );
      }
    }
    Utils.mixpanelTrack(MXP_EVENT.ADD_TO_LIST_PERFORMED, {
      pageType: PAGE_TYPE.discover,
      listName: list?.name,
      searchCriteria: config?.data || {},
      success
    });
  };

  /**
   * Build payload for POST /cm/contacts/ (add to directory without email = no credits).
   * Maps discover contact shape to directory shape; only includes contacts with linkedIn.
   * Includes company name and company domain for the backend Contact entity.
   */
  buildAddToDirectoryPayload = (contacts, list) => {
    const getLinkedInUrl = c => {
      const v = c?.linkedIn ?? c?.linkedin ?? c?.link ?? "";
      return typeof v === "string" ? v.trim() : "";
    };
    const getCompanyDomain = c =>
      c?.companyDomain ?? c?.primaryDomain ?? c?.companyWebsite ?? null;
    const mapped = (contacts || [])
      .filter(c => getLinkedInUrl(c) !== "")
      .map(c => {
        const raw = getLinkedInUrl(c);
        const linkedinId = raw ? (Utils.removeHttpFromString(raw).trim() || raw) : "";
        const domain = getCompanyDomain(c);
        return linkedinId
          ? {
              firstName: c?.firstName ?? null,
              lastName: c?.lastName ?? null,
              company: c?.companyName ?? null,
              domain: domain && typeof domain === "string" ? domain.trim() : null,
              jobTitle: c?.title ?? null,
              industry:
                typeof (c?.companyIndustry ?? c?.industry) === "string"
                  ? (c?.companyIndustry ?? c?.industry).trim() || null
                  : null,
              linkedinId,
              email: null,
              city: c?.city ?? null,
              state: c?.state ?? null,
              country: c?.country ?? null,
              liCompanyId: c?.companyLinkedIn ?? null,
              aiArkId: c?.id ?? null
            }
          : null;
      })
      .filter(Boolean);
    const listId = list?.id ?? list?._id ?? null;
    return {
      contacts: mapped,
      contactTags: [],
      listId: listId != null ? String(listId) : null,
      useSnCredits: true
    };
  };

  getPurchaseContactPayload = (list, purchaseType) => {
    const ids = [];
    this.selectedContactData.map(id => {
      ids.push(id);
    });
    return {
      purchasePayload: getContactPurchasePayload(ids, list?.id, purchaseType)
    };
  };

  getBulkPurchaseContactPayload = purchaseType => {
    const ids = [];
    this.selectedContactData.map(id => {
      ids.push(id);
    });
    return getContactPurchasePayload(ids, null, purchaseType);
  };

  @action
  searchFilterWithAPI = async (searchValue = "", autoCompleteVal = "") => {
    if (searchValue) {
      if (autoCompleteVal?.toLowerCase() === "industry" || autoCompleteVal?.toLowerCase() === "industryexclude") {
        const catalog = await getIndustriesCatalog();
        const filteredList = (catalog?.industries || [])
          .filter(item => item.toLowerCase().includes(searchValue.toLowerCase()))
          .slice(0, 50)
          .map(item => ({
            id: item,
            name: item.replace(/\b\w/g, l => l.toUpperCase())
          }));
        return { data: filteredList };
      }
      if (autoCompleteVal?.toLowerCase() === "technologies") {
        const list = await getTechnologiesList();
        const excludeFromDisplay = new Set(["mobile friendly"]);
        const filteredList = (list || [])
          .filter(item => !excludeFromDisplay.has(String(item || "").trim().toLowerCase()))
          .filter(item => item.toLowerCase().includes(searchValue.toLowerCase()))
          .slice(0, 50)
          .map(item => ({
            id: item,
            name: item.replace(/\b\w/g, l => l.toUpperCase())
          }));
        return { data: filteredList };
      }

      const urlObj = {
        companydomain: URLS.companyNameList,
        companylocation: URLS.locationNameList,
        location: URLS.locationNameList,
        keywords: URLS.keywordNameList,
        companykeywords: URLS.keywordNameList,
        contactkeywords: URLS.keywordNameList,
        companykeywordsexclude: URLS.keywordNameList,
        contactkeywordsexclude: URLS.keywordNameList
      };
      let url = `${urlObj[autoCompleteVal?.toLowerCase()]
        }?search=${searchValue}`;
      const config = {
        url,
        method: "POST"
      };
      checkAndCancelRequest();
      let res = await makeApi(config, cancelApiRequest);
      return res;
    } else {
      return {};
    }
  };

  @action
  getIndustriesData = async () => {
    const config = { url: URLS.industriesListV2, method: "GET" };
    let res = await makeApi(config);
    const { data = [], status = "" } = res || {};
    if (status === 200) {
      this.setIndustriesList(
        (data?.industries || []).map(item => ({ name: item, mapped_industries: [] }))
      );
    }
  };

  @action
  exportContactsAsCSV = async () => {
    if (dataSearchState?.selectedContactData?.length) {
      try {
        exportProcessingState.setShowPopup(true);
        let payload = getSelectedContactsPayload(
          dataSearchState?.selectedContactData
        );
        payload.emailPurchase = true;
        const config = {
          url: URLS.exportContactsAsCSV,
          method: "POST",
          data: payload
        };
        let res = await makeApi(config);
        const { data = {}, status = "" } = res || {};
        if (status === 200 && Utils.hasObject(data)) {
          if (this.isFromSeoPage) {
            this.setIsEmailViewedFromSeoPage(true);
            dataSearchState.setIsFromSeoPage(true);
          }
          handlePurchaseResponse(data, "", "", "export");
        } else {
          exportProcessingState.setShowPopup(false);
          toasterState.setToastMsg(
            "Failed to download selected contacts. Please try again later.",
            "fail"
          );
        }
        Utils.mixpanelTrack(MXP_EVENT.EXPORT_CSV_PERFORMED, {
          pageType: PAGE_TYPE.discover,
          searchCriteria: payload,
          success: status === 200
        });
      } catch (err) {
        console.error("Failed to export contacts", err);
      }
    }
  };

  @action
  addContactsToSequence = async (sequenceId = "", currentSequence = "") => {
    if (sequenceId && dataSearchState?.selectedContactData?.length) {
      try {
        ContactCardFunction.uploadingContact();
        let payload = getAddContactsToSequencePayload(
          dataSearchState?.selectedContactData,
          sequenceId
        );
        const config = {
          url: URLS.dsAddToSequence,
          method: "POST",
          data: payload
        };
        let res = await makeApi(config);
        const { data = {}, status = "" } = res || {};
        if (status === 200 && Utils.hasObject(data)) {
          if (this.isFromSeoPage) {
            this.setIsEmailViewedFromSeoPage(true);
            dataSearchState.setIsFromSeoPage(true);
          }
          handlePurchaseResponse(data, "", "", "sequence", currentSequence);
        } else {
          ContactCardFunction.resetUploadingFunc();
          toasterState.setToastMsg(
            "Failed to download selected contacts. Please try again later.",
            "fail"
          );
        }
        Utils.mixpanelTrack(MXP_EVENT.ADD_TO_SEQ_PERFORMED, {
          pageType: PAGE_TYPE.discover,
          searchCriteria: payload,
          success: status === 200
        });
      } catch (err) {
        console.error("Failed to export contacts", err);
      }
    }
  };
}

const dataSearchState = new DataSearchState();

export { dataSearchState, getTechnologiesList, getIndustriesCatalog };
